/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * The track is a native scroll-snap container, so scrolling, touch/trackpad swiping and
 * keyboard focus all work without JS. This component enhances it with:
 *  - prev/next arrows (disabled at the ends),
 *  - a hairline progress bar ([data-carousel-progress]) that follows the scroll position,
 *  - autoplay ([data-autoplay] = milliseconds per step): advances one item at a time and
 *    loops back to the start. It pauses while hovered, focused or touched, while off
 *    screen or in a background tab, and is never started for prefers-reduced-motion.
 *    [data-carousel-toggle] is the visible pause/play control (WCAG 2.2.2).
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel [data-autoplay="5000"]>
 *     [<button data-carousel-prev> / <button data-carousel-next> / <button data-carousel-toggle>]
 *     <div data-carousel-track> …items… </div>
 *     [<span data-carousel-progress>]
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");
    this.toggleButton = this.querySelector("[data-carousel-toggle]");
    this.progress = this.querySelector("[data-carousel-progress]");

    if (!this.track) return;

    this.onPrev = () => { this.nudge(); this.scrollByPage(-1); };
    this.onNext = () => { this.nudge(); this.scrollByPage(1); };
    this.onScroll = () => this.updateControls();
    this.onToggle = () => { this.userPaused = !this.userPaused; this.syncToggle(); this.schedule(); };
    this.onHold = () => { this.held = true; this.schedule(); };
    this.onRelease = () => { this.held = false; this.nudge(); };
    this.onVisibility = () => this.schedule();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.toggleButton?.addEventListener("click", this.onToggle);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.interval = parseInt(this.dataset.autoplay || "0", 10);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (this.interval > 0 && !reduced) {
      this.autoplay = true;
      // Hover does not pause (visitors stop to watch); touch and keyboard focus do, briefly.

      this.addEventListener("focusin", (event) => { if (event.target.matches(":focus-visible")) this.onHold(); });
      this.addEventListener("focusout", this.onRelease);
      this.track.addEventListener("touchstart", this.onHold, { passive: true });
      this.track.addEventListener("touchend", this.onRelease, { passive: true });
      document.addEventListener("visibilitychange", this.onVisibility);
      this.intersection = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.schedule();
      }, { threshold: 0.15 });
      this.intersection.observe(this);
    } else if (this.toggleButton) {
      this.toggleButton.hidden = true;
    }

    this.syncToggle();
    this.updateControls();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.toggleButton?.removeEventListener("click", this.onToggle);
    this.track?.removeEventListener("scroll", this.onScroll);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.resizeObserver?.disconnect();
    this.intersection?.disconnect();
    clearTimeout(this.resumeTimer)
    clearTimeout(this.timer);
  }

  /** Manual interaction (arrows, swipe end): hold autoplay for a few seconds, then resume. */
  nudge() {
    this.held = true;
    this.schedule();
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => { this.held = false; this.schedule(); }, 7000);
  }

  /** (Re)arm the autoplay timer only when every condition allows it. */
  schedule() {
    clearTimeout(this.timer);
    const running = this.autoplay && this.inView && !this.held && !this.userPaused && !document.hidden;
    this.toggleAttribute("data-playing", Boolean(running));
    if (!running) return;
    this.timer = setTimeout(() => {
      this.step();
      this.schedule();
    }, this.interval);
  }

  /** Advance one item; at the end, glide back to the first. */
  step() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll <= 1) return;
    const dir = getComputedStyle(this).direction === "rtl" ? -1 : 1;
    const atEnd = Math.abs(this.track.scrollLeft) >= maxScroll - 4;
    if (atEnd) {
      this.track.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    const item = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const amount = (item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8) * dir;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  syncToggle() {
    if (!this.toggleButton) return;
    this.toggleButton.setAttribute("aria-pressed", this.userPaused ? "true" : "false");
    this.toggleButton.setAttribute("aria-label", this.userPaused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause);
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** Disable prev/next at the ends and move the progress hairline. */
  updateControls() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const position = Math.abs(this.track.scrollLeft);
    const atStart = position <= 1;
    const atEnd = position >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
    if (this.progress) {
      const visible = overflowing ? this.track.clientWidth / this.track.scrollWidth : 1;
      const ratio = overflowing ? position / maxScroll : 0;
      this.progress.style.setProperty("--carousel-thumb", String(visible));
      this.progress.style.setProperty("--carousel-pos", String(ratio));
      this.progress.hidden = !overflowing;
    }
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
